import { useState, useEffect } from "react";
import { ShoppingCart, Wallet, Bell, Search, MapPin, Loader2 } from "lucide-react";
import logo from "../../Assests/Images/logo.png";
import {
  Box,
  Typography,
  IconButton,
  Badge,
  Button,
  Container,
  Tooltip,
} from "@mui/material";
import { toast } from "sonner";
import { HeaderStyles } from "../../Styles/layoutStyles";
import { useTheme } from "@mui/material/styles";
import { MotionBox } from "../../Componets/ui/MotionBox";
import { useNavigate } from "react-router-dom";
import Cart from "../../Pages/Cart/cart";
import { commonServices } from "../../Services/commonServices";
import { apiHandler } from "@shared/Functions/apiHandler";
import LogoutModel from "./logoutModel";
import { useCart } from "../../Context/useCart";
import { useDrawerOpenFun } from "../../Context/modelContext";
import BVNDrawer from "../../Pages/wallet/bvnCreate";
import SearchDrawer from "../../Pages/search/search";
import { EventSourcePolyfill } from "event-source-polyfill";
import { ApiConfig } from "@shared/Config/config";
import LoginModel from "../../Pages/loginModel";
import LocationDeny from "../../Pages/Home/locationDeny";
import LocationPickerModal from "../../Pages/Home/locationPickerModal";
import { useCheckLogin } from "../../utils/functions";
import ExitToAppIcon from '@mui/icons-material/ExitToApp';

export default function Header({ }) {
  const theme = useTheme();
  const classes = HeaderStyles(theme);
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const { cartCount, setCartCount } = useCart();
  const {
    cartOpen,
    setCartOpen,
    bvnDrawer,
    setBvnDrawer,
    searchDrawer,
    setSearchDrawer,
    location,
    setLocation,
    loginModal
  } = useDrawerOpenFun();

  const [notificationCount, setNotificationCount] = useState(0);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const token = sessionStorage.getItem("token");
  const [locationStatus, setLocationStatus] = useState(false);
  // custom hook for check login
  const checkLogin = useCheckLogin();

  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
      const data = await response.json();
      return data.address.city || data.address.town || data.address.village || "Unknown Location";
    } catch (error) {
      return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    }
  };

  const handleUpdateLocation = () => {
    setIsLocationModalOpen(true);
  };

  const handleSelectLocation = async (lat, lng, description) => {
    setLocation(prev => ({ ...prev, loading: true }));
    try {

      // 2. Determine address text
      let addressText = description;
      if (!addressText) {
        addressText = await reverseGeocode(lat, lng);
      }

      setLocation({
        address: addressText,
        loading: false,
        lat,
        lng
      });
      toast.success("Location updated successfully!");

      // 1. Update backend
      if (token) {
        await commonServices.updateLatLongs({ lat, long: lng });
      }

    } catch (error) {
      setLocation(prev => ({ ...prev, loading: false }));
      toast.error("Failed to update location");
    }
  };

  // Logout Function
  const onLogout = () => {
    sessionStorage.clear();
    navigate("/");
  };

  const getProfile = async () => {
    const res = await commonServices.getProfile();
    apiHandler(res);
    setProfileData(res?.data?.data?.cutomerDetails);
    setCartCount(res?.data?.data?.cutomerDetails?.cartCount);
  };

  const sendCoordinates = async (lat, long) => {
    if (!token) return;
    const res = await commonServices.updateLatLongs({ lat, long });
    apiHandler(res);
  }

  const fallbackIPLocation = async () => {
    try {
      const res = await fetch("https://ipapi.co/json/");
      const data = await res.json();

      if (data && data?.latitude && data?.longitude) {
        sendCoordinates(data?.latitude, data?.longitude);
        setLocation({
          address: data?.city,
          loading: false,
          lat: data?.latitude,
          lng: data?.longitude
        });
      } else {
        toast.error("Unable to detect your location on this device.");
        setIsLocationModalOpen(true);
      }
    } catch {
      toast.error("Location detection failed.");
      setIsLocationModalOpen(true);
    }
  };

  const locationAccess = () => {

    if (!navigator.geolocation) {
      console.warn("Geolocation is not supported by this browser.");
      toast.error("Geolocation is not supported.");
      return;
    }

    // Safari-compatible approach (skip Permissions API)
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        sendCoordinates(lat, lng);
        const city = await reverseGeocode(lat, lng);
        setLocation({ address: city, loading: false, lat, lng });
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          setLocationStatus(true);
          toast.error("Please enable location access in browser settings.");
          setIsLocationModalOpen(true);
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          fallbackIPLocation();
          // setIsLocationModalOpen(true);
        } else if (err.code === err.TIMEOUT) {
          fallbackIPLocation();
          // toast.error("Unable to detect your location on this device.");
          // setIsLocationModalOpen(true);
        }
      },
      { enableHighAccuracy: true, timeout: 2000, maximumAge: 0 }
    );

  };

  const handleSearch = () => {
    checkLogin(() => setSearchDrawer(true));
  };

  const walletHandler = () => {
    checkLogin(() => setBvnDrawer(true));
  };

  const notificationHandler = () => {
    checkLogin(() => navigate("/main/notification"));
  };

  const cartHandler = () => {
    checkLogin(() => setCartOpen(true));
  };

  const locationModelClose = () => {
    if (location.lat && location.lng) {
      setIsLocationModalOpen(false);
    } else {
      toast.error("Please Update your location.");
    }
  };

  useEffect(() => {
    if (token) {
      const source = new EventSourcePolyfill(
        `${ApiConfig.baseUrl}/customer/notificationCountStream`,
        {
          headers: {
            Authorization: `${token}`,
          },
          withCredentials: true, // include cookies if needed
        }
      );

      source.onmessage = (event) => {
        const data = JSON.parse(event.data);
        setNotificationCount(data?.data?.count);
      };

      source.onerror = (err) => {
        console.error("EventSource Error:", err);
      };
      return () => source.close();
    }
  }, []);

  useEffect(() => {
    token && getProfile();
    if (!location.lat) {
      locationAccess();
    }
  }, [cartOpen, location, loginModal]);

  return (
    <Container maxWidth="xl">

      <Cart
        open={cartOpen}
        onClose={() => setCartOpen(false)}
      />

      <LogoutModel
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
        onLogout={onLogout}
      />

      <BVNDrawer
        open={bvnDrawer}
        onClose2={() => setBvnDrawer(false)} />

      <SearchDrawer
        open={searchDrawer}
        onCloseModel={() => setSearchDrawer(false)}
        location={location}
      />

      <LoginModel />

      <LocationPickerModal
        open={isLocationModalOpen}
        onClose={locationModelClose}
        onSelectLocation={handleSelectLocation}
      />

      <MotionBox delay={0.1} y={-30} sx={classes.mainDiv}>
        {/* Left - Logo and Welcome Section */}
        <Box display="flex" alignItems="center" gap={2}>
          <Box sx={classes.imgDiv} onClick={() => navigate("/home")}>
            <img src={logo} alt="Logo" style={classes.imgClass} />
          </Box>
          <Box>
            <Typography sx={classes.welcomeText}> Welcome! </Typography>
            <Typography sx={classes.userText}> {profileData?.name} </Typography>
          </Box>
        </Box>

        {/* Right - Actions */}
        <Box display="flex" alignItems="center" gap={2}>

          <IconButton onClick={handleSearch}>
            <Search style={classes.headerIcon} />
          </IconButton>

          <IconButton onClick={walletHandler}>
            <Wallet style={classes.headerIcon} />
          </IconButton>

          <Badge
            badgeContent={notificationCount}
            color="warning"
            overlap="circular"
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            sx={{ cursor: "pointer", mx: 1 }}
            onClick={notificationHandler}
          >
            <Bell size={20} style={classes.headerIcon} />
          </Badge>

          <IconButton onClick={cartHandler}>
            <Badge
              badgeContent={cartCount}
              color="warning"
              overlap="circular"
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <ShoppingCart style={classes.headerIcon} />
            </Badge>
          </IconButton>

          {/* Location Picker */}
          <Tooltip title="Update current location" arrow>
            <Box sx={{ ...classes.locationBox, display: { xs: 'none', sm: 'flex' } }} onClick={handleUpdateLocation}>
              {location.loading ? (
                <Loader2 size={16} style={classes.spin} />
              ) : (
                <MapPin size={16} style={classes.locationIcon} />
              )}
              <Typography sx={classes.locationText}>
                {location.loading ? "Detecting..." : location.address}
              </Typography>
            </Box>
          </Tooltip>


          {token &&
            <Tooltip title="Logout" arrow>
              <Button
                onClick={() => setOpenDialog(true)}
                variant="contained"
                sx={{
                  ...classes.logoutBtn, // spread your existing styles
                  display: { xs: "none", md: "block" },
                }}
              >
                <ExitToAppIcon />
              </Button>
            </Tooltip>
          }
        </Box>
      </MotionBox>

      {locationStatus && (
        <MotionBox delay={0.1}>
          <LocationDeny />
        </MotionBox>
      )}

    </Container>
  );
}
